import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../layout/Layout';

interface BodyType {
  id: number;
  name: string;
  created_at: string;
}

const BodyTypes: React.FC = () => {
  const [bodyTypes, setBodyTypes] = useState<BodyType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    id: null as number | null,
    name: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchBodyTypes();
  }, []);

  const fetchBodyTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/admin/app-assets/body-types');
      if (response.data.success) {
        setBodyTypes(response.data.data.body_types);
      }
    } catch (error) {
      console.error('Error fetching body types:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (bodyType?: BodyType) => {
    if (bodyType) {
      setFormData({ id: bodyType.id, name: bodyType.name });
    } else {
      setFormData({ id: null, name: '' });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({ id: null, name: '' });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a name');
      return;
    }

    try {
      setSubmitting(true);
      if (formData.id) {
        await axios.put(`/admin/app-assets/body-types/${formData.id}`, {
          name: formData.name
        });
        alert('Body type updated successfully!');
      } else {
        await axios.post('/admin/app-assets/body-types', {
          name: formData.name
        });
        alert('Body type created successfully!');
      }
      closeModal();
      await fetchBodyTypes();
    } catch (error: any) {
      console.error('Error saving body type:', error);
      alert(error.response?.data?.message || 'Failed to save body type');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      await axios.delete(`/admin/app-assets/body-types/${id}`);
      alert('Body type deleted successfully!');
      await fetchBodyTypes();
    } catch (error: any) {
      console.error('Error deleting body type:', error);
      alert(error.response?.data?.message || 'Failed to delete body type');
    }
  };

  return (
    <Layout>
      <div>
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <div>
                <h4 className="mb-1 font-size-24 fw-semibold" style={{ color: '#495057' }}>Body Types</h4>
                <p className="text-muted mb-0">Manage user body type preferences</p>
              </div>
              <div className="page-title-right">
                <button className="btn btn-primary" onClick={() => openModal()}>
                  <i className="bx bx-plus me-1"></i>
                  Add New
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : bodyTypes.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bx bx-body font-size-48 text-muted mb-3 d-block"></i>
                    <p className="text-muted">No body types found</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Created Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bodyTypes.map(bodyType => (
                          <tr key={bodyType.id}>
                            <td>{bodyType.id}</td>
                            <td className="fw-semibold">{bodyType.name}</td>
                            <td>
                              <small>{new Date(bodyType.created_at).toLocaleDateString()}</small>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-warning me-2"
                                onClick={() => openModal(bodyType)}
                              >
                                <i className="bx bx-edit"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(bodyType.id, bodyType.name)}
                              >
                                <i className="bx bx-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {formData.id ? 'Edit Body Type' : 'Add New Body Type'}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeModal}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name *</label>
                    <input 
                      type="text" 
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter body type name"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bx bx-save me-1"></i>
                        Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BodyTypes;
